import { useSurveyObservation } from './SurveyObservationContext';
import { paymentCurrency } from './paymentCurrency';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Collapse from '@mui/material/Collapse';
import ExpandMore from '@mui/icons-material/ExpandMore';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import HtmlText from '../components/HtmlText';

import type { FlowComponentRenderProps, TippingComponentConfig } from '../types';
import ApruvTermsNotice from '../../components/common/ApruvTermsNotice';
import {
  centsToDollars,
  dollarsToCents,
  getTipFeeBreakdown,
  isValidTipAmount,
} from '../../utils/tipFee';
import {
  confirmPublicStripeTipCheckout,
  createPublicStripeTipCheckout,
  getPublicStripeTipConfig,
  loadStripeJS,
  type StripeEmbeddedCheckout,
  type CXPublicStripeTipStatus,
} from '../../services/cxPublicStripeTipService';
import TippingAmountSelector from './FeedbackTipAmountSelector';
import TippingFeeSummary from './FeedbackTipFeeSummary';
import TippingPaymentOptions from './FeedbackTipPaymentOptions';
import type { TippingPaymentOption, TippingValue } from '../components/tipping/types';

function parseCustomAmount(value: string): number | undefined {
  const parsed = Number.parseFloat(value);
  return isValidTipAmount(parsed) ? parsed : undefined;
}

const defaultTippingValue: TippingValue = {
  coverProcessingFee: false,
  paymentOption: 'card',
};

// Owned by the canonical renderer, not saved answer input. Only provider confirmation
// writes confirmedValue. This survives visit-key remounts without trusting authored data.
export interface TipPaymentRecovery {
  pendingSession?: string;
  confirmedValue?: TippingValue;
  attempt?: { signature: string; key: string };
  confirmations: Map<string, Promise<CXPublicStripeTipStatus>>;
}
export const createTipPaymentRecovery = (): TipPaymentRecovery => ({
  confirmations: new Map(),
});

const FeedbackTippingCard: React.FC<
  FlowComponentRenderProps<TippingComponentConfig> & {
    paymentRecovery?: TipPaymentRecovery;
    showCollapsedContinue?: boolean;
    amountSelectionFirst?: boolean;
    onInlineSkip?: () => void;
    onInlineTipConfirmed?: () => void;
  }
> = ({ config, paymentRecovery, showCollapsedContinue = true, amountSelectionFirst = false, onInlineSkip, onInlineTipConfirmed, goNext, onAction, onInteract, setValue, value, values }) => {
  const copyRef = useRef(config.paymentCopy);
  copyRef.current = config.paymentCopy;
  const copy = useCallback(
    (key: string, fallback: string) => copyRef.current?.[key] ?? fallback,
    []
  );
  const money = (cents: number) => paymentCurrency(cents / 100, config.paymentLocale);
  const localRecovery = useRef<TipPaymentRecovery>(createTipPaymentRecovery());
  const recovery = paymentRecovery ?? localRecovery.current;
  const [checkoutState, setCheckoutState] = useState<
    'selecting' | 'loading' | 'checkout' | 'confirming'
  >('selecting');
  const [expanded, setExpanded] = useState(config.initiallyCollapsed !== true);
  const [hasMadeAmountSelection, setHasMadeAmountSelection] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [paid, setPaid] = useState(!!recovery.confirmedValue);
  const [unconfirmedSession, setUnconfirmedSession] = useState<string | null>(
    recovery.pendingSession ?? null
  );
  const checkoutMountRef = useRef<HTMLDivElement | null>(null);
  const checkoutRef = useRef<StripeEmbeddedCheckout | null>(null);
  const returnSessionHandled = useRef(false);
  const observe = useSurveyObservation();
  const observedConfirmations = useRef(new WeakSet<Promise<CXPublicStripeTipStatus>>());
  const checkoutBusy = useRef(false);
  const confirmingSessions = recovery.confirmations;
  const paidValue = useRef<TippingValue | null>(recovery.confirmedValue ?? null);
  const confirmedSessions = useRef(new Set<string>());
  const checkoutAttempt = useRef<{ signature: string; key: string } | null>(
    recovery.attempt ?? null
  );
  const mounted = useRef(true);
  const checkoutOperation = useRef(0);
  const tippingValue = {
    ...defaultTippingValue,
    ...((value ?? {}) as TippingValue),
  };
  const hasCustomAmount = config.allowCustomAmount !== false && !!tippingValue.customAmount?.trim();
  const presetAmount = tippingValue.amount;
  const amount = hasCustomAmount
    ? parseCustomAmount(tippingValue.customAmount ?? '')
    : isValidTipAmount(presetAmount)
      ? presetAmount
      : undefined;
  const minimum = Math.max(1, config.minimumAmount ?? 1);
  const maximum = config.maximumAmount ?? Number.MAX_SAFE_INTEGER;
  const amountInRange =
    amount !== undefined &&
    amount >= minimum &&
    amount <= maximum &&
    Number.isSafeInteger(Math.round(amount * 100)) &&
    Math.abs(amount * 100 - Math.round(amount * 100)) < 1e-7 &&
    (config.allowCustomAmount !== false ||
      config.amounts.some((preset) => preset.value === amount));
  const customAmountBelowMinimum = hasCustomAmount && !amountInRange;
  const feeBreakdown = getTipFeeBreakdown(amount ?? 0, !!tippingValue.coverProcessingFee);
  const amountLabel = money(feeBreakdown.amountCents);
  const termsEnabled = config.termsEnabled !== false;
  const surveyCode = typeof values.cxSurveyCode === 'string' ? values.cxSurveyCode.trim() : '';

  const updateValue = (nextValue: TippingValue) => {
    setHasMadeAmountSelection(true);
    const mergedValue = {
      ...defaultTippingValue,
      ...tippingValue,
      ...nextValue,
    };
    const hasNextCustomAmount = !!mergedValue.customAmount?.trim();
    const nextPresetAmount = mergedValue.amount;
    const nextAmount = hasNextCustomAmount
      ? (parseCustomAmount(mergedValue.customAmount ?? '') ?? 0)
      : isValidTipAmount(nextPresetAmount)
        ? nextPresetAmount
        : 0;
    const nextBreakdown = getTipFeeBreakdown(nextAmount, !!mergedValue.coverProcessingFee);

    setValue(config.id, {
      ...mergedValue,
      amount: nextAmount,
      processingFee: centsToDollars(nextBreakdown.processingFeeCents),
      technicianTip: centsToDollars(nextBreakdown.technicianTipCents),
      total: centsToDollars(nextBreakdown.customerTotalCents),
    });
  };

  const finishPaidSession = useCallback(
    async (sessionId: string, operation: number) => {
      if (!mounted.current || checkoutOperation.current !== operation) return;
      if (!surveyCode)
        throw new Error(copy('payment.noLink', 'This survey does not have a payment link.'));
      if (confirmedSessions.current.has(sessionId)) return;
      recovery.pendingSession = sessionId;
      setUnconfirmedSession(sessionId);
      const confirmation =
        confirmingSessions.get(sessionId) ?? confirmPublicStripeTipCheckout(surveyCode, sessionId);
      confirmingSessions.set(sessionId, confirmation);
      const started = performance.now();
      const reportConfirmation = (outcome: 'success' | 'failure') => {
        if (observedConfirmations.current.has(confirmation)) return;
        observedConfirmations.current.add(confirmation);
        observe({
          category: 'payment',
          detail: 'confirmation',
          outcome,
          durationMs: performance.now() - started,
        });
      };
      try {
        setCheckoutError(null);
        setCheckoutState('confirming');
        const result = await confirmation;
        if (!mounted.current || checkoutOperation.current !== operation) return;
        if (result.componentId && result.componentId !== config.id)
          throw new Error(
            copy('payment.foreign', 'This payment belongs to a different tipping card.')
          );
        if (result.paymentStatus !== 'paid')
          throw new Error(copy('payment.unconfirmed', 'Your tip has not been confirmed.'));
        if (confirmedSessions.current.has(sessionId)) return;
        const amounts = [
          result.selectedAmountCents,
          result.totalAmountCents,
          result.processingFeeCents,
          result.technicianTipCents,
        ];
        if (
          !amounts.every((value) => Number.isSafeInteger(value) && value! > 0) ||
          !['card', 'cash_app'].includes(result.paymentOption ?? '')
        ) {
          throw new Error(
            copy(
              'payment.detailsUnavailable',
              'The confirmed tip details are unavailable. Please retry confirmation.'
            )
          );
        }
        const confirmedValue = {
          amount: centsToDollars(result.selectedAmountCents!),
          total: centsToDollars(result.totalAmountCents!),
          processingFee: centsToDollars(result.processingFeeCents!),
          technicianTip: centsToDollars(result.technicianTipCents!),
          coverProcessingFee: result.totalAmountCents! > result.selectedAmountCents!,
          paymentOption: result.paymentOption!,
          stripePaymentStatus: result.paymentStatus,
          stripeSessionId: result.sessionId,
          stripeTransactionStatus: result.transactionStatus,
        };
        recovery.confirmedValue = confirmedValue;
        recovery.pendingSession = undefined;
        paidValue.current = confirmedValue;
        confirmedSessions.current.add(sessionId);
        reportConfirmation('success');
        setUnconfirmedSession(null);
        setPaid(true);
        setCheckoutState('selecting');
        setValue(config.id, confirmedValue);
        onInlineTipConfirmed?.();
        const interaction = {
          componentId: config.id,
          type: 'tip' as const,
          value: paidValue.current?.amount ?? true,
        };
        if (onInteract) onInteract(interaction);
        else onAction?.(interaction);

        const returnUrl = new URL(window.location.href);
        if (returnUrl.searchParams.has('stripe_tip_session_id')) {
          returnUrl.searchParams.delete('stripe_tip_session_id');
          window.history.replaceState({}, '', returnUrl.toString());
        }
        if (!onInteract) goNext();
      } catch (error) {
        if (mounted.current && checkoutOperation.current === operation)
          reportConfirmation('failure');
        throw error;
      } finally {
        if (confirmingSessions.get(sessionId) === confirmation)
          confirmingSessions.delete(sessionId);
      }
    },
    [
      config.id,
      goNext,
      onAction,
      onInteract,
      onInlineTipConfirmed,
      setValue,
      surveyCode,
      recovery,
      confirmingSessions,
      observe,
    ]
  );

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      returnSessionHandled.current = false;
      checkoutOperation.current += 1;
      checkoutRef.current?.destroy();
      checkoutRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!config.checkoutEnabled || !surveyCode || returnSessionHandled.current) return;
    if (recovery.confirmedValue) return;
    const sessionId =
      recovery.pendingSession ??
      new URL(window.location.href).searchParams.get('stripe_tip_session_id');
    returnSessionHandled.current = true;
    if (!sessionId) return;
    const operation = ++checkoutOperation.current;
    void finishPaidSession(sessionId, operation).catch((error: unknown) => {
      if (!mounted.current || checkoutOperation.current !== operation) return;
      setCheckoutState('selecting');
      setCheckoutError(
        error instanceof Error
          ? error.message
          : copy('payment.confirmFailed', 'We could not confirm your tip. Please try again.')
      );
    });
  }, [config.checkoutEnabled, finishPaidSession, surveyCode, recovery, confirmingSessions]);

  const startCheckout = async () => {
    if (
      unconfirmedSession !== null ||
      !amountInRange ||
      !amount ||
      !surveyCode ||
      checkoutState !== 'selecting' ||
      checkoutBusy.current
    )
      return;
    checkoutBusy.current = true;
    const signature = JSON.stringify([
      surveyCode,
      config.id,
      amount,
      !!tippingValue.coverProcessingFee,
      tippingValue.paymentOption ?? 'card',
    ]);
    if (checkoutAttempt.current?.signature !== signature) {
      checkoutAttempt.current = {
        signature,
        key:
          typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `00000000-0000-4000-8000-${Date.now().toString(16).padStart(12, '0').slice(-12)}`,
      };
    }
    recovery.attempt = checkoutAttempt.current;
    const idempotencyKey = checkoutAttempt.current.key;
    const operation = ++checkoutOperation.current;
    setCheckoutError(null);
    setCheckoutState('loading');
    const started = performance.now();

    try {
      const [stripeConfig, stripeFactory, checkoutSession] = await Promise.all([
        getPublicStripeTipConfig(surveyCode),
        loadStripeJS(),
        createPublicStripeTipCheckout(surveyCode, {
          componentId: config.id,
          coverProcessingFee: !!tippingValue.coverProcessingFee,
          idempotencyKey,
          paymentOption: tippingValue.paymentOption ?? 'card',
          selectedAmountCents: dollarsToCents(amount),
        }),
      ]);
      if (!mounted.current || checkoutOperation.current !== operation) return;
      const stripe = stripeFactory(stripeConfig.publishableKey, {
        stripeAccount: stripeConfig.connectedAccountId,
      });
      const checkout = await stripe.initEmbeddedCheckout({
        clientSecret: checkoutSession.clientSecret,
        onComplete: () => {
          void finishPaidSession(checkoutSession.sessionId, operation).catch((error: unknown) => {
            if (!mounted.current || checkoutOperation.current !== operation) return;
            setCheckoutState('selecting');
            setCheckoutError(
              error instanceof Error
                ? error.message
                : copy(
                    'payment.completedUnconfirmed',
                    'Your payment completed, but the tip could not be confirmed. Please try again.'
                  )
            );
          });
        },
      });

      if (!mounted.current || checkoutOperation.current !== operation) {
        checkout.destroy();
        return;
      }
      checkoutRef.current?.destroy();
      checkoutRef.current = checkout;
      setCheckoutState('checkout');
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
      if (!mounted.current || checkoutOperation.current !== operation) return;
      if (!checkoutMountRef.current)
        throw new Error(copy('payment.displayFailed', 'Stripe checkout could not be displayed.'));
      checkout.mount(checkoutMountRef.current);
      observe({
        category: 'payment',
        detail: 'checkout',
        outcome: 'success',
        durationMs: performance.now() - started,
      });
    } catch (error: unknown) {
      if (!mounted.current || checkoutOperation.current !== operation) return;
      observe({
        category: 'payment',
        detail: 'checkout',
        outcome: 'failure',
        durationMs: performance.now() - started,
      });
      checkoutRef.current?.destroy();
      checkoutRef.current = null;
      setCheckoutState('selecting');
      setCheckoutError(
        error instanceof Error
          ? error.message
          : copy('payment.startFailed', 'Stripe checkout could not be started.')
      );
    } finally {
      checkoutBusy.current = false;
    }
  };

  const skipTipping = () => {
    if (onInlineSkip && !needsPaymentAttention) {
      setExpanded(false);
      onInlineSkip();
      return;
    }
    if (config.collapsible && expanded && !needsPaymentAttention) {
      setExpanded(false);
      return;
    }
    // Keep the uncertain provider session for recovery, but invalidate late navigation.
    checkoutOperation.current += 1;
    const interaction = {
      componentId: config.id,
      type: 'continue' as const,
      value: false,
    };
    if (onInteract) onInteract(interaction);
    else {
      onAction?.(interaction);
      goNext();
    }
  };

  const needsPaymentAttention =
    checkoutState !== 'selecting' || paid || !!unconfirmedSession || !!checkoutError;
  const isExpanded = !config.collapsible || expanded || needsPaymentAttention;
  const disclosureId = `tip-options-${config.id}`;

  return (
    <Card
      sx={{
        bgcolor: 'transparent',
        border: 0,
        borderRadius: 0,
        boxShadow: 'none',
        color: 'var(--cx-brand-text)',
        p: 0,
      }}
    >
      {config.collapsible && (
        <Button
          fullWidth
          aria-expanded={isExpanded}
          aria-controls={disclosureId}
          disabled={needsPaymentAttention}
          onClick={() => setExpanded(!isExpanded)}
          endIcon={<ExpandMore sx={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }} />}
          sx={{
            justifyContent: 'space-between',
            minHeight: 48,
            textTransform: 'none',
            color: 'var(--cx-brand-text)',
            fontWeight: 600,
          }}
        >
          <HtmlText html={config.title || 'Leave an optional tip'} component="span" />
        </Button>
      )}
      <Collapse in={isExpanded} timeout="auto">
        <Stack id={disclosureId} spacing={2} useFlexGap>
          {checkoutError ? (
            <Alert severity="error">
              {checkoutError}
              {unconfirmedSession ? (
                <Box sx={{ mt: 1 }}>
                  {copy(
                    'payment.recoveryHelp',
                    'You can retry confirmation or continue the survey. Continuing does not cancel or confirm this payment. Another checkout is disabled while its outcome is uncertain.'
                  )}
                </Box>
              ) : null}
              {unconfirmedSession && (
                <Button
                  onClick={() => {
                    void finishPaidSession(unconfirmedSession, checkoutOperation.current).catch(
                      (error: unknown) => {
                        if (!mounted.current) return;
                        setCheckoutState('selecting');
                        setCheckoutError(
                          error instanceof Error
                            ? error.message
                            : copy(
                                'payment.confirmFailed',
                                'We could not confirm your tip. Please try again.'
                              )
                        );
                      }
                    );
                  }}
                >
                  {copy('payment.retry', 'Retry confirmation')}
                </Button>
              )}
            </Alert>
          ) : null}

          {checkoutState === 'loading' || checkoutState === 'confirming' ? (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
                minHeight: 240,
              }}
            >
              <Stack spacing={1.5} sx={{ alignItems: 'center' }}>
                <CircularProgress size={32} />
                <Box sx={{ color: 'var(--cx-brand-text-muted)', fontWeight: 700 }}>
                  {checkoutState === 'confirming'
                    ? copy('payment.confirming', 'Confirming your tip…')
                    : copy('payment.opening', 'Opening secure checkout…')}
                </Box>
                {checkoutState === 'confirming' && (
                  <>
                    <Box sx={{ color: 'var(--cx-brand-text-muted)' }}>
                      {copy(
                        'payment.uncertain',
                        'Continuing does not cancel or confirm this payment.'
                      )}
                    </Box>
                    <Button onClick={skipTipping}>
                      {copy('payment.continueWaiting', 'Continue without waiting')}
                    </Button>
                  </>
                )}
              </Stack>
            </Box>
          ) : null}

          <Box sx={{ display: checkoutState === 'checkout' ? 'block' : 'none' }}>
            <Box ref={checkoutMountRef} sx={{ minHeight: 420 }} />
            <Button
              color="inherit"
              fullWidth
              onClick={() => {
                checkoutOperation.current += 1;
                checkoutRef.current?.destroy();
                checkoutRef.current = null;
                setCheckoutState('selecting');
              }}
              type="button"
              variant="text"
            >
              {copy('payment.changeTip', 'Change tip')}
            </Button>
          </Box>

          {paid ? (
            <Stack spacing={1}>
              <Alert severity="success">
                {copy('payment.confirmed', 'Your tip is confirmed. Thank you!')}
              </Alert>
              <Button
                variant="contained"
                onClick={() => {
                  const interaction = {
                    componentId: config.id,
                    type: 'tip' as const,
                    value: paidValue.current?.amount ?? true,
                  };
                  if (onInteract) onInteract(interaction);
                  else {
                    onAction?.(interaction);
                    goNext();
                  }
                }}
              >
                {copy('payment.continue', 'Continue')}
              </Button>
            </Stack>
          ) : checkoutState === 'selecting' ? (
            <Stack spacing={0.75}>
              <fieldset
                disabled={unconfirmedSession !== null}
                style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}
              >
                <Stack spacing={2} useFlexGap>
                  {config.title && !config.collapsible ? (
                    <HtmlText
                      html={config.title}
                      component="div"
                      sx={{
                        fontSize: 16,
                        fontWeight: 600,
                        lineHeight: '24px',
                        textAlign: 'center',
                      }}
                    />
                  ) : null}
                  <TippingAmountSelector
                    amount={amount}
                    amountLabel={amountLabel}
                    config={config}
                    customAmount={tippingValue.customAmount}
                    customAmountBelowMinimum={customAmountBelowMinimum}
                    onChange={updateValue}
                    customAmountAsOption={amountSelectionFirst}
                    hideCustomUntilSelected={amountSelectionFirst && !hasMadeAmountSelection}
                  />

                  <Collapse in={!amountSelectionFirst || amountInRange} timeout="auto" unmountOnExit>
                  <Stack spacing={2} useFlexGap>
                  <TippingPaymentOptions
                    copy={config.paymentCopy}
                    labels={config.paymentMethodLabels}
                    helpText={config.paymentHelpText}
                    onChange={(paymentOption: TippingPaymentOption) =>
                      updateValue({ paymentOption })
                    }
                    paymentOption={tippingValue.paymentOption ?? 'card'}
                  />

                  {termsEnabled ? (
                    <Box
                      data-feedback-tip-terms
                      sx={{
                        '&& .MuiTypography-caption': {
                          fontSize: 12,
                          lineHeight: '18px',
                          color: 'var(--cx-brand-text-muted)',
                        },
                        '&& .MuiTypography-caption button': { fontWeight: 600 },
                      }}
                    >
                      <ApruvTermsNotice
                        textAlign="left"
                        linkDecoration="none"
                        trailingPunctuation=""
                        linkColor="var(--cx-brand-text, #111827)"
                        closeLabel={config.paymentCopy?.['payment.termsClose']}
                        closeAccessibleLabel={config.paymentCopy?.['payment.termsCloseAccessible']}
                        linkLabel={
                          config.paymentCopy?.['payment.termsLink'] ?? config.termsLinkLabel
                        }
                        modalTitle={
                          config.paymentCopy?.['payment.termsTitle'] ?? config.termsModalTitle
                        }
                        termsHtml={config.termsHtml}
                        text={config.paymentCopy?.['payment.termsText'] ?? config.termsText}
                      />
                    </Box>
                  ) : null}

                  <TippingFeeSummary
                    copy={config.paymentCopy}
                    amountLabel={amountLabel}
                    coverProcessingFee={!!tippingValue.coverProcessingFee}
                    onCoverProcessingFeeChange={(coverProcessingFee) =>
                      updateValue({ coverProcessingFee })
                    }
                    processingFeeLabel={money(feeBreakdown.processingFeeCents)}
                    technicianTipLabel={money(feeBreakdown.technicianTipCents)}
                    totalLabel={money(feeBreakdown.customerTotalCents)}
                  />

                  {config.checkoutEnabled ? (
                    <Stack spacing={0.75} sx={{ pt: 0.5 }}>
                      <Button
                        disableElevation
                        disabled={!amountInRange || !surveyCode || unconfirmedSession !== null}
                        fullWidth
                        onClick={() => void startCheckout()}
                        sx={{
                          minHeight: 48,
                          fontSize: 15,
                          fontWeight: 600,
                          textTransform: 'none',
                          bgcolor: 'var(--cx-brand-button-selected-bg)',
                          color: 'var(--cx-brand-button-selected-text)',
                          '&:hover': {
                            bgcolor: 'var(--cx-brand-button-selected-bg)',
                          },
                        }}
                        type="button"
                        variant="contained"
                      >
                        {config.continueLabel?.trim() || copy('payment.continue', 'Continue')}
                      </Button>
                    </Stack>
                  ) : null}
                  </Stack>
                  </Collapse>
                </Stack>
              </fieldset>
              {config.checkoutEnabled || onInlineSkip ? (
                <Button
                  color="inherit"
                  sx={{
                    minHeight: 48,
                    textTransform: 'none',
                    fontSize: 15,
                    fontWeight: 600,
                    color: 'var(--cx-brand-text-muted)',
                  }}
                  fullWidth
                  onClick={skipTipping}
                  type="button"
                  variant="text"
                >
                  {config.skipLabel?.trim() || 'Skip tipping'}
                </Button>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      </Collapse>

      {!isExpanded && config.checkoutEnabled && showCollapsedContinue && (
        <Button fullWidth color="inherit" onClick={skipTipping} sx={{ minHeight: 48, textTransform: 'none' }}>
          {copy('payment.continue', 'Continue')}
        </Button>
      )}
    </Card>
  );
};

export default FeedbackTippingCard;
